import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, Landmark, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { RecheckStripeButton, fmtUsd } from './stuckInvoices';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stat = ({ label, value, tone = 'text-white', testId }) => (
  <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-lg font-semibold ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

const ChargeStatus = ({ row }) => {
  if (row.status === 'succeeded' && row.paid) {
    return row.stuck
      ? <Badge className="bg-red-500/20 text-red-300 border-red-500/30 gap-1"><AlertTriangle className="h-3 w-3" /> Paid · not recorded</Badge>
      : <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">Succeeded</Badge>;
  }
  if (row.status === 'pending') return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">Pending (ACH)</Badge>;
  return <Badge variant="outline" className="border-white/20 text-slate-300 capitalize">{row.status}</Badge>;
};

/** Owner Admin: charges Stripe actually processed on this workspace's connected account vs our ledger. */
export default function WorkspaceStripeTruth({ workspaceId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/api/owner-admin/platform/workspaces/${encodeURIComponent(workspaceId)}/stripe-truth`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `Failed (${r.status})`);
      setData(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const acct = data?.account;

  return (
    <Card className="bg-card border-white/15" data-testid="stripe-truth-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-white flex items-center gap-2"><Landmark className="h-5 w-5 text-emerald-400" /> Stripe Truth</CardTitle>
          <CardDescription>
            Live from Stripe: every charge on this workspace&apos;s connected account (last {data?.lookback_days || 120} days) and what CountBucks earned on each.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="border-white/20" data-testid="stripe-truth-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 bg-white/10" />)}</div>}
        {error && <p className="text-sm text-red-300" data-testid="stripe-truth-error">{error}</p>}
        {data && !data.connected && <p className="text-sm text-amber-300" data-testid="stripe-truth-not-connected">{data.message}</p>}
        {data?.error && <p className="text-sm text-amber-300" data-testid="stripe-truth-account-error">{data.error}</p>}

        {data?.connected && acct && (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="stripe-truth-account">
              <span className="font-mono text-slate-300">{acct.id}</span>
              {acct.business_name && <span>· {acct.business_name}</span>}
              {acct.email && <span>· {acct.email}</span>}
              <Badge variant="outline" className={acct.charges_enabled ? 'border-emerald-500/40 text-emerald-300' : 'border-red-500/40 text-red-300'}>
                charges {acct.charges_enabled ? 'enabled' : 'disabled'}
              </Badge>
              <Badge variant="outline" className={acct.payouts_enabled ? 'border-emerald-500/40 text-emerald-300' : 'border-amber-500/40 text-amber-300'}>
                payouts {acct.payouts_enabled ? 'enabled' : 'disabled'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Stripe gross (succeeded)" value={fmtUsd(t.stripe_gross_usd)} testId="stripe-truth-gross" />
              <Stat label="Your income (app fees)" value={fmtUsd(t.countbucks_income_usd)} tone="text-emerald-300" testId="stripe-truth-income" />
              <Stat label="CountBucks ledger gross" value={fmtUsd(t.countbucks_ledger_gross_usd)} testId="stripe-truth-ledger-gross" />
              <Stat
                label="Paid on Stripe, not recorded"
                value={t.unrecorded_paid_charges || 0}
                tone={t.unrecorded_paid_charges ? 'text-red-300' : 'text-emerald-300'}
                testId="stripe-truth-unrecorded"
              />
            </div>

            {data.charges.length === 0 ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="stripe-truth-empty">
                <CheckCircle2 className="h-4 w-4 text-slate-400" /> Stripe shows no charges on this account in the window — nothing has been paid through it.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Date</TableHead>
                    <TableHead className="text-muted-foreground">Invoice</TableHead>
                    <TableHead className="text-muted-foreground text-right">Charged</TableHead>
                    <TableHead className="text-muted-foreground text-right">Your fee</TableHead>
                    <TableHead className="text-muted-foreground">Method</TableHead>
                    <TableHead className="text-muted-foreground">Stripe</TableHead>
                    <TableHead className="text-muted-foreground">CountBucks</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.charges.map((row) => (
                    <TableRow key={row.charge_id} className={`border-white/10 ${row.stuck ? 'bg-red-500/5' : ''}`} data-testid={`stripe-truth-row-${row.charge_id}`}>
                      <TableCell className="text-slate-300 whitespace-nowrap">{new Date(row.created).toLocaleDateString()}</TableCell>
                      <TableCell className="text-white">
                        {row.via_countbucks ? (
                          <>
                            #{row.invoice_no ?? '?'} <span className="text-slate-400">{row.invoice_customer || ''}</span>
                            {row.invoice_workspace_matches === false && <span className="block text-[11px] text-amber-300">invoice belongs to another workspace</span>}
                          </>
                        ) : (
                          <span className="text-amber-300 text-xs">Outside CountBucks{row.description ? ` · ${row.description}` : ''}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-white">
                        {fmtUsd(row.amount)}
                        {row.amount_refunded > 0 && <span className="block text-[11px] text-red-300">-{fmtUsd(row.amount_refunded)} refunded</span>}
                      </TableCell>
                      <TableCell className="text-right text-emerald-300">{fmtUsd(row.application_fee)}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{row.payment_method_type === 'us_bank_account' ? 'ACH' : row.payment_method_type || '—'}</TableCell>
                      <TableCell><ChargeStatus row={row} /></TableCell>
                      <TableCell className="text-xs">
                        {row.via_countbucks
                          ? <span className={row.invoice_status === 'paid' ? 'text-emerald-300' : 'text-red-300'}>invoice {row.invoice_status || 'missing'}{row.recorded_in_countbucks ? ' · on ledger' : ''}</span>
                          : <span className="text-slate-500">n/a</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.stuck && row.invoice_id && <RecheckStripeButton invoiceId={row.invoice_id} onDone={load} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <p className="text-[11px] text-muted-foreground/70">
              &quot;Your fee&quot; is the Stripe application fee CountBucks collected ({'0.6%, $25 cap by default'}). Rows flagged red were paid on Stripe but never recorded here — Re-check fixes them.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
